<template>
  <b-form ref="userForm">
    <b-row>
      <b-col cols="12" lg="10" xl="6">
        <template v-if="loading">
          <loader />
        </template>
        <template v-else>
          <b-form-group
            id="input-group-1"
            :label="$t('first-name')"
            label-for="input-1"
          >
            <b-form-input
              id="input-1"
              v-model="form.user.first_name"
              type="text"
            />
          </b-form-group>
          <b-form-group
            id="input-group-2"
            :label="$t('last-name')"
            label-for="input-2"
          >
            <b-form-input
              id="input-2"
              v-model="form.user.last_name"
              type="text"
            />
          </b-form-group>
          <b-form-group
            id="input-group-3"
            :label="$t('username')"
            label-for="input-3"
          >
            <b-form-input
              id="input-3"
              v-model="form.user.username"
              type="text"
              required
            />
          </b-form-group>
          <b-form-group
            id="input-group-4"
            :label="$t('email')"
            label-for="input-4"
          >
            <b-form-input
              id="input-4"
              v-model="form.user.email"
              type="email"
              required
            />
          </b-form-group>
          <b-form-group
            id="input-group-5"
            :label="$t('phone')"
            label-for="input-5"
          >
            <b-form-input
              id="input-5"
              v-model="form.user.phone"
              type="text"
            />
          </b-form-group>
          <b-form-group
            v-if="this.getUserData.user.can_change_password"
            :label="$t('password-change')"
            label-size="lg"
            label-class="font-weight-bold py-1"
          >
            <b-form-group
              id="password"
              :label="$t('password')"
              label-for="input-6"
            >
              <b-form-input
                id="password-6"
                v-model="form.user.password"
                type="password"
              />
            </b-form-group>
            <b-form-group
              id="repeat-password"
              :label="$t('repeat-password')"
              label-for="input-6"
            >
              <b-form-input
                id="repeat-password"
                v-model="repeatedPassword"
                type="password"
                :invalid-feedback="$t('passwords-do-not-match')"
                valid-feedback="Ok"
                :state="repeatedPasswordValid"
              />
            </b-form-group>
          </b-form-group>
          <b-form-group
            :label="$t('choose-language')"
            label-size="lg"
            label-class="font-weight-bold py-1"
          >
            <b-form-select
              v-model="form.user.language"
              :options="languagesOptions"
            >
            </b-form-select>
          </b-form-group>
          <b-form-group
            :label="$t('appearance')"
            label-size="lg"
            label-class="font-weight-bold py-1"
          >
            <b-form-group
              :label="$t('starting-screen')"
            >
              <b-form-select
                v-model="startingScreen"
                :options="startingScreenOptions"
              >
              </b-form-select>
              <p v-if="startingScreen === 'visualization'" class="mt-2">
                {{ $t('starting-visualization-is') }}:
                <span v-if="getDefaultSpacePlan">{{ getDefaultSpacePlan.name }}</span>
                <span v-else>{{ $t('not-set') }}</span>
                <b-button class="mx-1" @click="openSetVisualizationModal">
                  {{ $t('choose') }}
                </b-button>
              </p>
              <set-default-visualization-modal-form ref="setDefaultVisualizationModalForm" />
            </b-form-group>
            <b-form-checkbox v-if="getUserData.enable_devices || getUserData.superuser"
                             id="show_devices"
                             v-model="form.show_devices"
            >
              {{ $t('show-devices') }}
            </b-form-checkbox>
            <b-form-checkbox v-if="getUserData.enable_instaler_level || getUserData.superuser"
                             id="show_instaler_level"
                             v-model="form.show_instaler_level"
            >
              {{ $t('show-instaler-level') }}
            </b-form-checkbox>
            <b-form-checkbox v-if="getUserData.enable_advance_level || getUserData.superuser"
                             id="show_advance_level"
                             v-model="form.show_advance_level"
            >
              {{ $t('show-advance-level') }}
            </b-form-checkbox>
          </b-form-group>
          <b-form-group
            :label="$t('notifications')"
            label-size="lg"
            label-class="font-weight-bold py-1"
          >
            <b-form-checkbox
              id="checkbox-2"
              v-model="form.enable_push_notifications"
              name="checkbox-2"
            >
              {{ $t('enable-push-notifications') }}
            </b-form-checkbox>
            <b-form-checkbox
              id="checkbox-3"
              v-model="form.enable_email_notifications"
              name="checkbox-3"
            >
              {{ $t('enable-email-notifications') }}
            </b-form-checkbox>
          </b-form-group>
          <b-form-group
            :label="$t('menu')"
            label-size="lg"
            label-class="font-weight-bold py-1"
          >
            <b-form-checkbox
              id="checkbox-4"
              v-model="form.enable_menu_collapsing"
              name="checkbox-4"
            >
              {{ $t('enable-menu-collapsing') }}
            </b-form-checkbox>
          </b-form-group>
          <b-form-group
            :label="$t('navigation')"
            label-size="lg"
            label-class="font-weight-bold py-1"
          >
            <b-form-checkbox
              id="checkbox-5"
              v-model="serviceGroupTileClickNavigatesToNextLevel"
              name="checkbox-5"
            >
              {{ $t('service-group-tile-click-navigates-to-next-level') }}
            </b-form-checkbox>
            <b-form-checkbox
              id="checkbox-6"
              v-model="serviceTileClickNavigatesToNextLevel"
              name="checkbox-"
            >
              {{ $t('service-tile-click-navigates-to-next-level') }}
            </b-form-checkbox>
          </b-form-group>
          <b-form-group class="d-flex justify-content-end">
            <b-button @click="dataUpdated">{{ $t('save') }}</b-button>
          </b-form-group>
        </template>
      </b-col>
    </b-row>
  </b-form>
</template>

<script>
import axios from 'axios';
import config from '@/config';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import BreadcrumbsManager from '@/mixins/BreadcrumbsManager';
import Loader from '@/components/utils/Loader.vue';
import ToasterMixin from '@/mixins/Toaster';
import eventBus from '@/components/utils/slideout-panel/eventBus';
import { SET_USER_DATA, UPDATE_NAVIGATION_SETTINGS } from '@/store/mutation-types';
import SetDefaultVisualizationModalForm
  from '@/components/user-management/SetDefaultVisualizationModalForm.vue';


export default {
  name: 'User',
  components: { SetDefaultVisualizationModalForm, Loader },
  data() {
    return {
      debounce: 1000,
      form: {
        user: {
          id: null,
          first_name: null,
          last_name: null,
          username: null,
          email: null,
          phone: null,
          language: null,
          password: null,
        },
        id: null,
        enable_push_notifications: null,
        enable_email_notifications: null,
        show_devices: null,
        show_advance_level: null,
        show_instaler_level: null,
        starting_screen: null,
        enable_menu_collapsing: null,
      },
      loading: false,
      repeatedPassword: '',
      languagesOptions: [
        {
          value: 1,
          text: 'English',
        },
        {
          value: 2,
          text: 'Česky',
        },
      ],
      startingScreenOptions: [
        {
          value: 'favorites',
          text: this.$t('favorite'),
        },
        {
          value: 'visualization',
          text: this.$t('vizualization'),
        },
      ],
      defaultLanguage: 'visualization',
      defaultStartingScreen: 'favorites',
      breadcrumbs: [
        {
          text: this.$t('user-settings'),
        },
      ],
    };
  },
  mixins: [
    BreadcrumbsManager,
    ToasterMixin,
  ],
  computed: {
    ...mapGetters('user', [
      'getUserData',
      'getDefaultSpacePlan',
    ]),
    ...mapGetters([
      'getDefaultSpacePlan',
    ]),
    ...mapState('snapshot', [
      'currentSnapshot',
    ]),
    ...mapState('app', [
      'navigationSettings',
    ]),
    repeatedPasswordValid() {
      if (this.form.user.password) {
        return this.repeatedPassword === this.form.user.password;
      }
      return null;
    },
    startingScreen: {
      get() {
        return this.form.starting_screen;
      },
      set(value) {
        this.form.starting_screen = value;
      },
    },
    serviceGroupTileClickNavigatesToNextLevel: {
      get() {
        return this.navigationSettings.serviceGroupTileClickNavigatesToNextLevel;
      },
      set(value) {
        this.updateNavigationSettings({
          serviceGroupTileClickNavigatesToNextLevel: value,
        });
      },
    },
    serviceTileClickNavigatesToNextLevel: {
      get() {
        return this.navigationSettings.serviceTileClickNavigatesToNextLevel;
      },
      set(value) {
        this.updateNavigationSettings({
          serviceTileClickNavigatesToNextLevel: value,
        });
      },
    },
  },
  methods: {
    ...mapActions('user', [
      'loadUserData',
    ]),
    ...mapMutations('user', {
      updateUser: SET_USER_DATA,
    }),
    ...mapMutations('app', {
      updateNavigationSettings: UPDATE_NAVIGATION_SETTINGS,
    }),
    dataUpdated() {
      const url = new URL(
        config.ICOOL.API.USERS_ME_PATH(this.currentSnapshot.id),
        config.ICOOL.BACKEND_HOST,
      ).href;
      const data = { ...this.form, project: this.currentSnapshot.project };
      if (data.user.password === '') {
        delete data.user.password; // validator doesn´t accept empty password
      }
      return new Promise((resolve, reject) => {
        if (!this.$refs.userForm.checkValidity()) {
          this.$refs.userForm.reportValidity();
          this.showToast(this.$t('user-update'), this.$t('form-contains-errors'), 'danger');
          resolve();
        } else {
          axios.patch(url, data)
            .then((response) => {
              this.showToast(this.$t('user-update'), this.$t('saved'), 'success');
              if (response.data.user.language !== (this.getUserData.user.language || this.defaultLanguage)) {
                eventBus.$emit('languageChanged', this.form.user.language);
              }
              this.updateUser(response.data);
              resolve();
            })
            .catch((error) => {
              this.showToast(this.$t('user-update'), this.$t('error'), 'danger');
              reject();
            })
            .finally(() => {
              this.form.user.password = '';
              this.repeatedPassword = '';
            });
        }
      });
    },
    initData() {
      this.form.user.id = this.getUserData.user.id;
      this.form.user.first_name = this.getUserData.user.first_name;
      this.form.user.last_name = this.getUserData.user.last_name;
      this.form.user.username = this.getUserData.user.username;
      this.form.user.email = this.getUserData.user.email;
      this.form.user.phone = this.getUserData.user.phone;
      this.form.user.language = this.getUserData.user.language || this.defaultLanguage;
      this.form.user.password = '';
      this.form.id = this.getUserData.id;
      this.form.enable_push_notifications = this.getUserData.enable_push_notifications;
      this.form.enable_email_notifications = this.getUserData.enable_email_notifications;
      this.form.show_devices = this.getUserData.show_devices;
      this.form.show_advance_level = this.getUserData.show_advance_level;
      this.form.show_instaler_level = this.getUserData.show_instaler_level;
      this.form.starting_screen = this.getUserData.starting_screen || this.defaultStartingScreen;
      this.form.enable_menu_collapsing = this.getUserData.enable_menu_collapsing;
    },
    openSetVisualizationModal() {
      this.$refs.setDefaultVisualizationModalForm.show();
    },
  },
  async beforeMount() {
    this.loading = true;
    await this.loadUserData();
    this.initData();
    this.loading = false;
  },
};
</script>

<style scoped>

</style>
